// Pure helper: turns a (truckPrefill, globalPrefill) pair into a tagged list of
// pill descriptors `[{ label, source }]` where `source` is `"truck"` if the field
// came from the per-truck strategy and `"global"` if it fell back to the shared
// strategy. Mirrors `mergePrefills` semantics — truck-non-null wins per-field.
//
// Iterates only over fields the schema actually provides — when the schema grows
// (e.g. freight types, multi-state pickup/destination, avoid lists), add cases here.
//
// Returns [] when both prefills are empty.

const formatTrailerType = (value) => {
  if (!value) return null;
  const lower = String(value).toLowerCase();
  if (lower === "dry_van" || lower === "dryvan" || lower === "van") return "Van";
  if (lower === "reefer") return "Reefer";
  if (lower === "flatbed") return "Flatbed";
  return String(value).charAt(0).toUpperCase() + String(value).slice(1).toLowerCase();
};

const formatNumber = (value) => {
  if (value == null) return null;
  return Number(value).toLocaleString("en-US");
};

const formatDistanceRange = (min, max) => {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `Distance ${formatNumber(min)}–${formatNumber(max)} mi`;
  if (min != null) return `Min ${formatNumber(min)} mi`;
  return `Max ${formatNumber(max)} mi`;
};

const pickField = (truckPrefill, globalPrefill, key) => {
  if (truckPrefill && truckPrefill[key] != null) {
    return { value: truckPrefill[key], source: "truck" };
  }
  if (globalPrefill && globalPrefill[key] != null) {
    return { value: globalPrefill[key], source: "global" };
  }
  return null;
};

const pickArrayField = (truckPrefill, globalPrefill, key) => {
  if (truckPrefill && truckPrefill[key] && truckPrefill[key].length > 0) {
    return { value: truckPrefill[key], source: "truck" };
  }
  if (globalPrefill && globalPrefill[key] && globalPrefill[key].length > 0) {
    return { value: globalPrefill[key], source: "global" };
  }
  return null;
};

const pushPill = (pills, label, source) => {
  if (label == null) return;
  pills.push({ label, source });
};

export const buildStrategyPills = (truckPrefill, globalPrefill) => {
  if (!truckPrefill && !globalPrefill) return [];

  const pills = [];

  const trailer = pickField(truckPrefill, globalPrefill, "trailerType");
  if (trailer) pushPill(pills, formatTrailerType(trailer.value), trailer.source);

  const departureZip = pickField(truckPrefill, globalPrefill, "departureZip");
  if (departureZip) pushPill(pills, `Pickup ${departureZip.value}`, departureZip.source);

  const pickupRadius = pickField(truckPrefill, globalPrefill, "pickupRadius");
  if (pickupRadius) pushPill(pills, `Pickup radius ${pickupRadius.value} mi`, pickupRadius.source);

  // Distance range: source = truck if either bound came from truck (truck is overriding).
  const distMin = pickField(truckPrefill, globalPrefill, "outboundDistanceMin");
  const distMax = pickField(truckPrefill, globalPrefill, "outboundDistanceMax");
  if (distMin || distMax) {
    const label = formatDistanceRange(distMin?.value, distMax?.value);
    const source = distMin?.source === "truck" || distMax?.source === "truck" ? "truck" : "global";
    pushPill(pills, label, source);
  }

  const minOffer = pickField(truckPrefill, globalPrefill, "minOffer");
  if (minOffer) pushPill(pills, `Min $${formatNumber(minOffer.value)}`, minOffer.source);

  const minRpm = pickField(truckPrefill, globalPrefill, "minRpm");
  if (minRpm) pushPill(pills, `Min $${Number(minRpm.value).toFixed(2)}/mi`, minRpm.source);

  const destinationZip = pickField(truckPrefill, globalPrefill, "destinationZip");
  if (destinationZip) pushPill(pills, `Destination ${destinationZip.value}`, destinationZip.source);

  const deliveryRadius = pickField(truckPrefill, globalPrefill, "deliveryRadius");
  if (deliveryRadius) pushPill(pills, `Delivery radius ${deliveryRadius.value} mi`, deliveryRadius.source);

  const preferredBrokers = pickArrayField(truckPrefill, globalPrefill, "preferredBrokers");
  if (preferredBrokers) {
    preferredBrokers.value.forEach((broker) => pushPill(pills, `Prefer ${broker}`, preferredBrokers.source));
  }

  const avoidedBrokers = pickArrayField(truckPrefill, globalPrefill, "avoidedBrokers");
  if (avoidedBrokers) {
    avoidedBrokers.value.forEach((broker) => pushPill(pills, `Avoid ${broker}`, avoidedBrokers.source));
  }

  const avoidedMarkets = pickArrayField(truckPrefill, globalPrefill, "avoidedMarkets");
  if (avoidedMarkets) {
    avoidedMarkets.value.forEach((market) => pushPill(pills, `Avoid ${market}`, avoidedMarkets.source));
  }

  return [...pills.filter((p) => p.source === "global"), ...pills.filter((p) => p.source === "truck")];
};
